<template>
  <div class="footer">
    <div
      id="footer-start"
      class="footer__content-start"
    >
      <BackByRoute
        v-if="!$route.meta?.hideBackButtonInFooter"
        :key="$route.fullPath"
        class="footer__back-button"
      />
    </div>
    <div
      id="footer-center"
      class="footer__content-start"
    >
      <SubscriptionAdminBanner
        v-if="showSubscriptionAdminBanner"
        :trialEndDate="trialEndDate"
      />
    </div>
    <span class="footer__content-end">
      <DisabledFeatureTooltip
        v-if="showAIButton"
        feature="aiAssistant"
        modalId="modal-enterprise-features"
        useAclField="accessToAi"
        title="Feature available to <br> Enterprise plan Users"
        class="footer__ai-setup"
        :class="{ 'is-active': isAiChatOpen }"
      >
        <template #label>
          <button
            v-if="!currentAiProfile.name && isAdmin"
            type="button"
            class="footer__ai-setup-btn"
            @click="onShowAISetupModal"
          >
            <AIResponseAgent name="AI Set Up" />
          </button>
          <button
            v-else-if="currentAiProfile.name"
            id="aiProfile"
            type="button"
            class="footer__ai-setup-btn"
            @click="onShowAIChatWindow"
          >
            <AIResponseAgent
              name="AI"
              :img="currentAiProfile.picture.pictureUrl"
            >
              <template #agent-name>
                <span class="footer__ai-name">AI</span>
              </template>
            </AIResponseAgent>
          </button>
        </template>
      </DisabledFeatureTooltip>
      <ToDoWidget
        v-if="$route.params?.accountId && accountId && !$route.meta?.hideToDoWidget"
        :key="keyByRoute"
      />
      <DropdownTooltip
        v-if="hasNavigationItems"
        iconSize="md"
        class="footer-navigation"
        :class="[
          { 'navigation-rotate': opened },
          { 'learn-tooltip pointer-none': step === selectedStep },
          { 'navigation-tramor': !opened }
        ]"
        :distance="8"
        :shown="showMenu || selectedStep === step"
        :autoHide="selectedStep !== step"
        :popperClass="`footer-navigation_poper  ${selectedStep === step ? 'pointer-none' : ''}`"
        placement="top-end"
        @onShowDropdown="toggleNavigation"
        @onHideDropdown="toggleNavigation"
      >
        <template #after>
          <TooltipLearn
            :step="step"
            :show="selectedStep === step"
            placement="left"
            :skidding="-40"
            :distance="150"
            :tooltipText="$t(`learnTooltipText.${step}`)"
          />
        </template>
        <template #tooltip-label>
          <icon
            data="@icon/addMark.svg"
            :fill="false"
            color="var(--accent-03)"
            width="var(--icon-24)"
            height="var(--icon-24)"
          />
        </template>
        <template #tooltip-content>
          <div class="footer-navigation_list">
            <div
              v-for="(nav, index) in navigationList"
              :key="index"
              v-close-popper
              class="footer-navigation_link"
              :class="[{ 'navigation_link-remove': !nav.show }]"
            >
              <template v-if="nav.show">
                <DisabledFeatureTooltip
                  v-if="nav.disabledByPlan"
                  :feature="nav.feature"
                  useAclField="hasSubscriptionIgnoreDemo"
                  class="footer-navigation_link__item"
                >
                  <template #label>
                    <div class="footer-navigation_link__shortcut">
                      <icon
                        v-if="nav.icon"
                        :data="nav.icon"
                        :fill="true"
                        width="var(--icon-24)"
                        height="var(--icon-24)"
                      />
                      <BaseSubHeading
                        size="large"
                        weight="400"
                      >
                        {{ nav.label }}
                      </BaseSubHeading>
                    </div>
                  </template>
                  <template #after>
                    <span class="footer-navigation_link__help">{{ nav.help }}</span>
                  </template>
                </DisabledFeatureTooltip>
                <router-link
                  v-else-if="nav.to"
                  class="footer-navigation_link__item"
                  :to="nav.to"
                >
                  <div class="footer-navigation_link__shortcut">
                    <icon
                      v-if="nav.icon"
                      :data="nav.icon"
                      :fill="true"
                      width="var(--icon-24)"
                      height="var(--icon-24)"
                    />
                    <BaseSubHeading
                      size="large"
                      weight="400"
                    >
                      {{ nav.label }}
                    </BaseSubHeading>
                  </div>
                  <span class="footer-navigation_link__help">{{ nav.help }}</span>
                </router-link>
                <button
                  v-else
                  type="button"
                  class="footer-navigation_link__item"
                  @click.prevent="handleBtnNavigation(nav)"
                >
                  <div class="footer-navigation_link__shortcut">
                    <icon
                      v-if="nav.icon"
                      :data="nav.icon"
                      :fill="true"
                      width="var(--icon-24)"
                      height="var(--icon-24)"
                    />
                    <BaseSubHeading
                      size="large"
                      weight="400"
                    >
                      {{ nav.label }}
                    </BaseSubHeading>
                  </div>
                  <span class="footer-navigation_link__help">{{ nav.help }}</span>
                </button>
              </template>
            </div>
          </div>
        </template>
      </DropdownTooltip>
    </span>
  </div>
  <Teleport to="body">
    <MCreateEpic
      :id="project.name"
      @onCreateEpic="createEpic"
    />
    <ModalInsufficientPermissions />
    <ModalBusinessFeatures />
    <ModalEnterpriseFeatures />
    <ModalAiChat
      :key="accountId"
      @opened="isAiChatOpen = true"
      @closed="isAiChatOpen = false"
    />
  </Teleport>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { useAcl } from "vue-simple-acl"
import backlogItemIcon from "@icon/backlog-item-24.svg"
import ideaIcon from "@icon/idea-24.svg"
import epicsIcon from "@icon/menu-epics-24.svg"
import projectIcon from "@icon/project-24.svg"
import goalIcon from "@icon/roadmap-goal-24.svg"
import sprintIcon from "@icon/sprint-24.svg"
import { directive as ClickOutside } from "click-outside-vue3"
import { Action, Getter } from "s-vuex-class"

import AIResponseAgent from "@/components/AiSetup/AIResponseAgent.vue"
import BackByRoute from "@/components/ControlButtons/BackByRoute.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import TooltipLearn from "@/components/Dropdown/TooltipLearn.vue"
import MCreateEpic from "@/components/Modal/MCreateEpic.vue"
import ModalInsufficientPermissions from "@/components/Modal/ModalInsufficientPermissions.vue"
import DisabledFeatureTooltip from "@/components/Subscription/DisabledFeatureTooltip.vue"
import SubscriptionAdminBanner from "@/components/Subscription/SubscriptionAdminBanner.vue"
import ToDoWidget from "@/components/ToDo/ToDoWidget.vue"

import isMacintosh from "@/helpers/checkMacintosh"
import { keys, KeyType } from "@/helpers/keys"
import {
  Account,
  AIProfile,
  Epic,
  Item,
  Project,
  ProjectOverview,
  StripePrice,
  Team,
  TipsStepType,
  User,
  UserSettings
} from "@/models"
import { PermissionActions, PermissionTypes } from "@/models/PermissionsType"
import { Actions, Getters } from "@/store"

import ModalAiChat from "../Modal/ModalAiChat.vue"
import ModalBusinessFeatures from "../Modal/ModalBusinessFeatures.vue"
import ModalEnterpriseFeatures from "../Modal/ModalEnterpriseFeatures.vue"

@Options({
  name: "Footer",
  components: {
    SubscriptionAdminBanner,
    ModalInsufficientPermissions,
    DropdownTooltip,
    MCreateEpic,
    ToDoWidget,
    BackByRoute,
    TooltipLearn,
    ModalBusinessFeatures,
    ModalEnterpriseFeatures,
    DisabledFeatureTooltip,
    AIResponseAgent,
    ModalAiChat
  },
  directives: {
    ClickOutside
  }
})
export default class Footer extends Vue {
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Getter(Getters.GET_AI_PROFILE) readonly currentAiProfile: AIProfile
  @Getter(Getters.GET_CURRENT_PROJECT_ID) readonly projectId: string
  @Getter(Getters.GET_PROJECT) readonly project: Project
  @Getter(Getters.GET_SESSION_TEAM) selectedTeam: Team
  @Getter(Getters.GET_ITEM) readonly item: Item
  @Getter(Getters.GET_ACCOUNT) readonly account: Account
  @Getter(Getters.GET_USER_CURRENT_PROFILE_SETTINGS) readonly userSettings: UserSettings
  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType
  @Getter(Getters.PROJECT_OVERVIEW) readonly projectOverview: ProjectOverview[]
  @Getter(Getters.CURRENT_STRIPE_PRICE) readonly currentPlan: StripePrice

  @Action(Actions.SET_PROJECTS) setProjects: (projects: Project[]) => void
  @Action(Actions.POST_EPIC) postEpic: (epic: { name: string }) => Epic
  @Action(Actions.CHECK_PERMISSION) setPermissionInfo: ({
    type,
    action
  }: {
    type: PermissionTypes
    action: PermissionActions
  }) => void
  @Getter(Getters.GET_ACCOUNT_ID) accountId: string

  opened = false
  isAiChatOpen = false
  showMenu = false
  showItemsBasedOnParams = {
    NewProject: ["accountId"],
    NewGoal: ["projectId"],
    NewIdea: ["projectId"],
    NewMilestone: ["projectId"],
    NewEpic: ["projectId"],
    NewSprint: ["projectId"],
    NewItem: ["projectId"]
  }

  step: TipsStepType = "footerMenu"

  get trialEndDate() {
    return this.account?.paymentSubscription?.trialEndDate
  }

  get showSubscriptionAdminBanner() {
    if (
      !this.trialEndDate ||
      this.account?.paymentSubscription?.status !== "TRIALING" ||
      !this.$route.params.accountId ||
      !useAcl().can("superAdmin") ||
      this.currentPlan?.productName !== "Team"
    ) {
      return false
    }
    return true
  }

  get noProjectAccess() {
    if (!this.$route.params.projectId || this.isAdmin || this.project.demo) return false
    const projects = this.currentUser.projects ?? []
    return projects.every(p => p.id !== this.projectId)
  }

  get navigationList() {
    const queryForCrateItem = { ...this.$route.query }
    const queryForCreateSprint = this.selectedTeam
      ? {
          t: this.selectedTeam.urlName
        }
      : {}
    delete queryForCrateItem.item
    const newItem = this.$route.meta.createStoryInBacklog
      ? {
          query: {
            ...this.$route.query,
            i: "create"
          },
          params: {
            projectId: this.project.urlName
          }
        }
      : {
          name: "CreateItem",
          params: {
            accountId: this.accountUrlId,
            projectId: this.project.urlName,
            teamId:
              this.$route.params?.teamId ||
              (this.$route.params?.itemId ? this.item?.team?.urlName : "") ||
              ""
          }
        }

    return [
      {
        id: "newProject",
        label: "New Project",
        icon: projectIcon,
        help: keys(KeyType.PROJECT),
        show: this.showMenuParam("NewProject"),
        keyCode: 80,
        to: this.isAdmin
          ? {
              name: "create-project",
              params: {
                accountId: this.accountUrlId
              }
            }
          : null,
        disabledByPlan:
          !useAcl().can("hasSubscriptionIgnoreDemo") &&
          this.account?.projectIdsIncludedInSubscription &&
          Object.keys(this.account.projectIdsIncludedInSubscription).length > 0,
        feature: "unlimitedProjects"
      },
      {
        id: "newGoal",
        label: "New Goal",
        icon: goalIcon,
        help: keys(KeyType.GOAL),
        show: this.showMenuParam("NewGoal"),
        keyCode: 71,
        to: {
          name: "roadmap",
          params: {
            projectId: this.project.urlName,
            accountId: this.accountUrlId
          },
          // The Roadmap opens the Goal wizard for this query and removes it.
          query: { createGoal: "true" }
        }
      },
      {
        id: "newIdea",
        label: "New Idea",
        icon: ideaIcon,
        help: keys(KeyType.IDEA),
        show: this.showMenuParam("NewIdea"),
        keyCode: 68,
        to: {
          name: "ideas",
          params: {
            projectId: this.project.urlName,
            accountId: this.accountUrlId
          },
          query: {
            newIdea: "true"
          }
        }
      },
      // {
      //   id: "newMilestone",
      //   label: "New Milestone",
      //   help: keys(KeyType.MILESTONE),
      //   show: this.showMenuParam("NewMilestone"),
      //   keyCode: 77,
      //   to: {
      //     name: "Milestones",
      //     params: {
      //       projectId: this.project.urlName,
      //       accountId: this.accountUrlId
      //     }
      //   },
      //   disabledByPlan: !useAcl().can("hasSubscriptionIgnoreDemo"),
      //   feature: "productMilestones"
      // },
      {
        id: "newEpic",
        label: "New Epic",
        icon: epicsIcon,
        help: keys(KeyType.EPIC),
        show: this.showMenuParam("NewEpic"),
        keyCode: 69,
        to: {
          name: "epics",
          params: {
            projectId: this.project.urlName,
            accountId: this.accountUrlId
          },
          query: {
            newEpic: "true"
          }
        }
      },
      {
        id: "newSprint",
        label: "New Sprint",
        icon: sprintIcon,
        help: keys(KeyType.SPRINT),
        show: this.showMenuParam("NewSprint") && !this.selectedTeam?.disableSprintCreation,
        keyCode: 83,
        to: {
          name: "create-sprint",
          params: {
            accountId: this.accountUrlId,
            projectId: this.project.urlName
          },
          query: queryForCreateSprint
        }
      },
      {
        id: "newItem",
        label: "New Item",
        icon: backlogItemIcon,
        help: keys(KeyType.ITEM),
        show: this.showMenuParam("NewItem"),
        keyCode: 73,
        to: newItem
      }
    ]
  }

  get hasNavigationItems(): boolean {
    return this.navigationList.some(nav => nav.show)
  }

  showMenuParam(id) {
    const params = Object.keys(this.$route.params)
    const query = Object.keys(this.$route.query)
    return this.showItemsBasedOnParams[id].every(param => {
      const showByParams = params.includes(param) || query.includes(param)
      if (param === "projectId" && this.noProjectAccess) {
        return false
      }
      return showByParams
    })
  }

  get showAIButton() {
    return (
      !this.$wait.is("resolveAccount") &&
      this.$route.params?.accountId &&
      this.accountId &&
      ((!this.currentAiProfile.name && this.isAdmin) || this.currentAiProfile.name)
    )
  }

  get accountUrlId(): string {
    if (this.$route.params?.accountId) {
      return this.$route.params?.accountId as string
    } else {
      return this.account ? this.account.urlName : ""
    }
  }

  get isAdmin() {
    return useAcl().can.any(["admin", "superAdmin"])
  }

  get isMacintosh(): boolean {
    return isMacintosh()
  }

  get keyByRoute() {
    return this.$route?.meta?.updateComponentByRoute
      ? decodeURI(this.$route.path).replace(/\s/g, "")
      : ""
  }

  mounted() {
    document.addEventListener("keydown", this.onKeyDown)
    document.addEventListener("keyup", this.onKeyDown)
  }

  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeyDown)
    document.removeEventListener("keyup", this.onKeyDown)
  }

  onKeyDown(e: KeyboardEvent) {
    let checkSystemKey = this.isMacintosh ? e.metaKey : e.ctrlKey
    const navTo = (this.navigationList ?? []).find(i => i.keyCode === e.keyCode)
    if (e.shiftKey && checkSystemKey && navTo?.keyCode) {
      e.preventDefault()

      if (navTo?.show && navTo?.to) {
        this.$router.push(navTo.to)
      }
    }

    if (e.shiftKey && checkSystemKey) {
      this.showMenu = true
    } else {
      this.showMenu = false
    }
  }

  toggleNavigation() {
    this.opened = !this.opened
  }

  async createEpic(name: string) {
    const epic = await this.postEpic({ name })
    await this.$router.push({
      name: "epic",
      params: {
        epicId: epic.sequenceNumber
      }
    })
  }

  handleBtnNavigation(nav) {
    if (nav.id === "newProject" && !this.isAdmin) {
      this.setPermissionInfo({ type: PermissionTypes.project, action: PermissionActions.add })
      this.$modal.show("modal-insufficient-permissions")
    }
  }

  onShowAISetupModal() {
    this.$modal.show("modal-ai-setup")
  }

  onShowAIChatWindow() {
    if (!this.isAiChatOpen) {
      this.$modal.show("modal-ai-chat")
    } else {
      this.$modal.hide("modal-ai-chat")
    }
  }
}
</script>
